import { useEffect, useState } from 'react';

import { DutyCode } from 'generated';
import { Control } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { formatISOString, formatTime } from 'utils/date';

import { queries } from 'RestAPIs/rapini';

import { Grid, TextField } from '@mui/material';

import ControlledDutyCodeSelector from 'components/shared/ControlledFields/ControlledDutyCodeSelector';
import { Option as DutyCodeOption } from 'components/shared/DutyCodeSelector';

export type Props = {
  control: Control<any, any>;
  disabled?: boolean;
  onDutyCodeSelect: (dutyCode?: DutyCode) => void;
  referenceDate?: Date;
};

function OverrideDutyCode({
  control,
  disabled,
  onDutyCodeSelect,
  referenceDate
}: Props) {
  const { t } = useTranslation('common');
  const [{ validFrom, validTo, periods }, setDutyCode] = useState<
    Omit<DutyCode, 'dutyCodeId'>
  >({
    validFrom: '',
    validTo: '',
    periods: [],
    periodsCanBeEdited: false,
    canBeDeleted: false
  });
  const [overrideDutyCode, setOverrideDutyCode] =
    useState<DutyCodeOption | null>(null);

  const { data: dutyCodes } = queries.useGetDutyCodes(
    referenceDate?.toISOString()
  );

  useEffect(() => {
    if (overrideDutyCode) {
      const selectedDutyCode = dutyCodes?.find(
        ({ dutyCodeId }) => dutyCodeId === overrideDutyCode.dutyCodeId
      );
      if (selectedDutyCode) {
        setDutyCode(selectedDutyCode);
        onDutyCodeSelect(selectedDutyCode);
      }
    } else {
      setDutyCode({
        validFrom: '',
        validTo: '',
        periods: [],
        periodsCanBeEdited: false,
        canBeDeleted: false
      });
      onDutyCodeSelect();
    }
  }, [overrideDutyCode, dutyCodes]);

  return (
    <Grid container spacing={2.5} mb={overrideDutyCode ? 2.5 : 0}>
      <Grid item xs={12}>
        <ControlledDutyCodeSelector
          control={control}
          name="overrideDutyCodeId"
          onChange={(e, value) => {
            setOverrideDutyCode(value);
          }}
          label={t('overrideDutyCode')}
          fullWidth
          disabled={disabled}
        />
      </Grid>
      <Grid item xs={6}>
        <TextField
          fullWidth
          variant="outlined"
          size="small"
          label={t('validFrom')}
          value={validFrom ? formatISOString(validFrom) : '-'}
          disabled
        />
      </Grid>
      <Grid item xs={6}>
        <TextField
          fullWidth
          variant="outlined"
          size="small"
          label={t('validTo')}
          value={validTo ? formatISOString(validTo) : '-'}
          disabled
        />
      </Grid>
      <Grid item xs={12}>
        <Grid container spacing={2.5}>
          {periods?.map((period, index) => (
            <>
              <Grid item xs={3}>
                <TextField
                  variant="outlined"
                  size="small"
                  label={index === 0 ? t('startTime') : ''}
                  value={formatTime(period.startTime)}
                  disabled
                />
              </Grid>
              <Grid item xs={3}>
                <TextField
                  variant="outlined"
                  size="small"
                  label={index === 0 ? t('endTime') : ''}
                  value={formatTime(period.endTime)}
                  disabled
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  variant="outlined"
                  size="small"
                  label={index === 0 ? t('dutyHourType') : ''}
                  value={period.dutyHourType?.description || '-'}
                  disabled
                  fullWidth
                />
              </Grid>
            </>
          ))}
        </Grid>
      </Grid>
    </Grid>
  );
}

export default OverrideDutyCode;
